import { Emdash } from "~/components/Punctuation";
import { ColumnMetadata, ColumnTypes } from "../../../../components/DataGrid";
import { NetworkTableData, NetworkTableDataColumns } from "../../types";
import { ImageLoader } from "~/components/ImageLoader";
import { DISTRIBUTION_TYPE_TOOLTIPS, RWA_COIN_ICON } from "~/consts";
import { DataGridDollarPercentChangeComboCell } from "~/components/DataGrid/DataGridDollarPercentChangeComboCell";
import { DataGridNumberPercentChangeComboCell } from "~/components/DataGrid/DataGridNumberPercentChangeComboCell";
import { getNetworkURL } from "~/routes";
import { NetworkUserEvents } from "~/modules/analytics/events/networks";
import { Link } from "~/components/Link";
import { PercentageBar } from "~/components/PercentageBar";
import { cn } from "~/lib/utils";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "~/components/Tooltip";

// // // //

export const RANK_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: "rank",
    label: "#",
    type: ColumnTypes.number,
    sticky: {
        left: 0,
        isLast: false,
    },
    width: 55,
    cellValue: (ntwrk) => {
        if (ntwrk.parent_network_id) {
            return null;
        }
        return ntwrk.rank;
    },
};

export const NAME_INDIVIDUAL_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: NetworkTableDataColumns.name,
    label: "Name",
    type: ColumnTypes.name,
    width: 230,
    sortsOn: NetworkTableDataColumns.name,
    sticky: {
        left: 55,
        isLast: true,
    },
    render: (ntwrk) => {
        const showSubnets = ntwrk.child_networks.length > 0;

        const subnetsButton = (
            <Tooltip
                placement="right"
                tooltipContent={
                    <div className="w-max px-2 py-0.5 text-center">
                        <p className="whitespace-nowrap text-xs font-light">
                            Show Avalanche L1s
                        </p>
                    </div>
                }
            >
                {({ setTriggerRef }) => (
                    <button
                        ref={setTriggerRef}
                        className="bg-slate-200 hover:bg-slate-300 transition-colors rounded-sm p-1 text-slate-600"
                        onClick={() => {
                            if (!ntwrk.onExpand) {
                                return;
                            }
                            ntwrk.onExpand();
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faChevronLeft}
                            className={cn(
                                "float-left w-3 h-3 transition-transform",
                                {
                                    "-rotate-90": ntwrk.expanded,
                                },
                            )}
                        />
                    </button>
                )}
            </Tooltip>
        );

        return (
            <div
                className={cn("flex items-center justify-start select-none", {
                    "gap-2": showSubnets,
                })}
            >
                <div className="flex items-start flex-col justify-center overflow-hidden">
                    <Link
                        href={getNetworkURL({ networkSlug: ntwrk.slug })}
                        analyticsLabel={NetworkUserEvents.TableLinkClick}
                        className="flex items-center gap-2"
                        variant="default"
                    >
                        <ImageLoader
                            src={ntwrk.icon_url || RWA_COIN_ICON}
                            alt={`${ntwrk.name} Icon`}
                            fallbackSrc={RWA_COIN_ICON}
                            className="h-4 w-4 min-w-4 rounded-full"
                        />
                        <span className="dg-overflow">{ntwrk.name}</span>
                    </Link>
                </div>

                {showSubnets && subnetsButton}
            </div>
        );
    },
};

// export const ASSET_COUNT_COLUMN: ColumnMetadata<
//     NetworkTableData,
//     NetworkTableDataColumns
// > = {
//     slug: NetworkTableDataColumns.assetCount,
//     label: "Total Asset Count",
//     type: ColumnTypes.number,
//     width: 130,
//     maxWidth: 130,
//     sortsOn: NetworkTableDataColumns.assetCount,
//     cellValue: (network) => {
//         return network.asset_count;
//     },
// };

export const RWA_ASSET_COUNT_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: NetworkTableDataColumns.rwaAssetCount,
    label: "RWA Count",
    type: ColumnTypes.number,
    width: 130,
    maxWidth: 130,
    sortsOn: NetworkTableDataColumns.rwaAssetCount,
    cellValue: (network) => {
        return network.rwa_asset_count;
    },
};

export const STABLECOIN_ASSET_COUNT_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: NetworkTableDataColumns.stablecoinAssetCount,
    label: "Stablecoin Asset Count",
    type: ColumnTypes.number,
    width: 130,
    maxWidth: 130,
    sortsOn: NetworkTableDataColumns.stablecoinAssetCount,
    cellValue: (network) => {
        return network.stablecoin_asset_count;
    },
};

export const RWA_MARKET_CAP_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: NetworkTableDataColumns.rwaMarketCap,
    label: (
        <>
            <p>RWA Total Value</p>
            <p className="text-xs">(Excl. Stablecoins)</p>
        </>
    ),
    type: ColumnTypes.dollar,
    sortsOn: NetworkTableDataColumns.rwaMarketCap,
    width: 175,
    render: (network) => {
        const measure = network.rwa_market_cap;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

// ENHANCEMENT - add 30D % change here
export const DISTRIBUTED_MARKET_CAP_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: NetworkTableDataColumns.distributedMarketCap,
    label: (
        <>
            <p>RWA Value</p>
            <p className="text-xs">(Distributed)</p>
        </>
    ),
    type: ColumnTypes.dollar,
    sortsOn: NetworkTableDataColumns.distributedMarketCap,
    tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
    width: 175,
    cellValue: (network) => {
        return network.transferability.transferable
            ? network.transferability.transferable
            : null;
    },
};

// ENHANCEMENT - add 30D % change here
export const ISOLATED_MARKET_CAP_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: NetworkTableDataColumns.isolatedMarketCap,
    label: (
        <>
            <p>RWA Value</p>
            <p className="text-xs">(Represented)</p>
        </>
    ),
    tooltip:
        "Total value of RWA tokens using the blockchain as a recordkeeping layer, enhancing transparency, reconciliation, and operational efficiency without enabling onchain investor transfer or distribution.",
    type: ColumnTypes.dollar,
    sortsOn: NetworkTableDataColumns.isolatedMarketCap,
    width: 175,
    cellValue: (network) => {
        return network.transferability.non_transferable
            ? network.transferability.non_transferable
            : null;
    },
};

export const PCT_DISTRIBURTED_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: NetworkTableDataColumns.transferabilityPct,
    label: "% Distributed",
    type: ColumnTypes.percent,
    sortsOn: NetworkTableDataColumns.transferabilityPct,
    width: 150,
    render: (network) => {
        return (
            <PercentageBar
                percentage={network.transferability.pct_transferable}
            />
        );
    },
};

export const STABLECOIN_MARKET_CAP_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: NetworkTableDataColumns.stablecoinMarketCap,
    label: "Stablecoins Market Cap",
    type: ColumnTypes.dollar,
    sortsOn: NetworkTableDataColumns.stablecoinMarketCap,
    width: 175,
    render: (network) => {
        const measure = network.stablecoin_market_cap;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const RWA_HOLDERS_COUNT_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: NetworkTableDataColumns.rwaHoldersCount,
    label: "RWA Holders",
    tooltip: "Excludes Stablecoins",
    type: ColumnTypes.number,
    sortsOn: NetworkTableDataColumns.rwaHoldersCount,
    width: 150,
    render: (network) => {
        const measure = network.rwa_holders_count;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridNumberPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const STABLECOIN_HOLDERS_COUNT_COLUMN: ColumnMetadata<
    NetworkTableData,
    NetworkTableDataColumns
> = {
    slug: NetworkTableDataColumns.stablecoinHoldersCount,
    label: "Stablecoin Holders",
    type: ColumnTypes.number,
    sortsOn: NetworkTableDataColumns.stablecoinHoldersCount,
    width: 150,
    render: (network) => {
        const measure = network.stablecoin_holders_count;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridNumberPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

// export const NEW_ISSUANCE_COLUMN: ColumnMetadata<
//     NetworkTableData,
//     NetworkTableDataColumns
// > = {
//     slug: NetworkTableDataColumns.dailyMintsDollar,
//     label: "Total New Issuance",
//     type: ColumnTypes.dollar,
//     sortsOn: NetworkTableDataColumns.dailyMintsDollar,
//     width: 150,
//     cellValue: (network) => {
//         return network.adjusted_daily_mints_dollar?.val || 0;
//     },
// };

// export const ACTIVE_ADDRESSES_COUNT_COLUMN: ColumnMetadata<
//     NetworkTableData,
//     NetworkTableDataColumns
// > = {
//     slug: NetworkTableDataColumns.trailing30DayActiveAddressesCount,
//     label: "Active Addresses",
//     type: ColumnTypes.number,
//     // sortsOn: NetworkTableDataColumns.trailing30DayActiveAddressesCount,
//     width: 150,
//     render: (network) => {
//         const measure = getMeasureValue(
//             network,
//             "trailing_30_day_active_addresses_count",
//         );

//         if (measure === null) {
//             return <Emdash />;
//         }

//         return (
//             <DataGridNumberPercentChangeComboCell
//                 value={measure.val}
//                 pct_change_interval="30d"
//                 pct_change_value={measure.chg_30d_pct}
//             />
//         );
//     },
// };

// export const TRANSFER_VOLUME_COLUMN: ColumnMetadata<
//     NetworkTableData,
//     NetworkTableDataColumns
// > = {
//     slug: NetworkTableDataColumns.trailing30DayTransferVolume,
//     label: "Transfer Volume",
//     type: ColumnTypes.dollar,
//     sortsOn: NetworkTableDataColumns.trailing30DayTransferVolume,
//     width: 175,
//     render: (network) => {
//         const measure = getMeasureValue(
//             network,
//             "trailing_30_day_transfer_volume",
//         );

//         if (measure === null) {
//             return <Emdash />;
//         }

//         return (
//             <DataGridDollarPercentChangeComboCell
//                 value={measure.val}
//                 pct_change_interval="30d"
//                 pct_change_value={measure.chg_30d_pct}
//             />
//         );
//     },
// };
